import { AfterViewInit, Component, DestroyRef, ElementRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EventOriginEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { Asset } from '@app/core/model/entities/asset/asset';
import { Section } from '@app/core/model/other/field-config';
import { FormStateService } from '@app/shared/components/form-builder/form-state.service';
import { GeneratesObject } from '@app/shared/interfaces/generates-object';
import { TranslateService } from '@ngx-translate/core';
import { SectionService } from '@services/section.service';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Component({
  templateUrl: './asset-update-asset-groups-modal.component.html',
  providers: [FormStateService],
  standalone: false
})
export class AssetUpdateAssetGroupsModalComponent implements OnInit, AfterViewInit, OnDestroy, GeneratesObject {
  public formId = 'asset_update_asset_groups_dialog';
  protected eventsOrigin = EventOriginEnum.SHEET;
  public permissionsForEdition = [PermissionEnum.EDIT_ASSET];
  protected missingRequiredFields$: Observable<{ code: string, label: string }[]>;
  protected form: Section[];

  protected formStateService = inject(FormStateService);
  private sectionService = inject(SectionService);
  private destroyRef = inject(DestroyRef);
  private elementRef = inject(ElementRef);
  private translate = inject(TranslateService);

  private assetGroupIds: string[] = [];
  private saveButtonObserver: MutationObserver;

  public ngOnInit(): void {
    this.missingRequiredFields$ = this.sectionService.getSectionsByFormCode(this.formId).pipe(
      tap(sections => this.form = sections),
      map(sections => sections.first()),
      map(section => this.sectionService.missingRequiredFields(section)),
      takeUntilDestroyed(this.destroyRef)
    );

    this.formStateService.saved$
      .pipe(
        tap(({data}) => {
          Asset.transformPropertiesForInput(data);
          this.assetGroupIds = data.assetGroupIds as string[];
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /**
   * The shared field-group builder (group-field-builder.component.html) hardcodes its "creation" save button to
   * BUTTON.CREATE. This dialog updates existing Assets rather than creating an entity, so relabel that button to
   * BUTTON.SAVE once it renders, without modifying the shared component. The button is re-created whenever the
   * field group's mode toggles, so a MutationObserver re-applies the label each time it reappears.
   */
  public ngAfterViewInit(): void {
    this.saveButtonObserver = new MutationObserver(() => this.relabelSaveButton());
    this.saveButtonObserver.observe(this.elementRef.nativeElement, {childList: true, subtree: true});
    this.relabelSaveButton();
  }

  public ngOnDestroy(): void {
    this.saveButtonObserver?.disconnect();
  }

  private relabelSaveButton(): void {
    const saveButton: HTMLElement = this.elementRef.nativeElement.querySelector('#save-button');
    const label = this.translate.instant('BUTTON.SAVE');
    if (saveButton && saveButton.textContent.trim() !== label) {
      saveButton.textContent = label;
    }
  }

  /**
   * Data from the filled form.
   * @return Ids of the selected Asset Groups.
   */
  public getGeneratedObject(): string[] {
    return this.assetGroupIds;
  }
}
